import { Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, useTheme } from "@mui/material";
import { Button, Typography } from "@foodpilot/foods";
import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useMyActionPlanContext } from "../../../context/useMyActionPlan";
import { useActionsContext } from "src/context/Actions/useActionsContext";
import { SsqPropertyAction } from "src/api/sure-api/actions/useGetOneAction";
import { useAnswerPrecisionQuestion } from "src/api/client-api/ActionPlan/ActionPlan/useAnswerPrecisionQuestion";
import { ActionParameterInput } from "src/components/Actions/Common/ActionParameterInput";
import { Loading } from "src/components/utils/Loading";
import { useLanguage } from "src/context/LanguageProvider";
import { findOtherActionsSharingProperty } from "./sharedPrecisionPropertyActions";
import { HTMLBox } from "src/components/HTMLBox/HTMLBox";

const getPrecisionProperties = (properties: SsqPropertyAction[]): SsqPropertyAction[] =>
    properties.filter((property) => property.visibleInActionParametrization === true);

type StartingPostForPrecision = {
    elements?: { ssqPropertyId?: number; isGenericValue?: boolean; value?: string | null }[];
};

const readPropertyDisplayValue = (property: SsqPropertyAction, startingPost: StartingPostForPrecision): string => {
    const element = startingPost.elements?.find((elem) => elem.ssqPropertyId === property.id);
    return (element?.isGenericValue ? property.genericValue : undefined) ?? element?.value ?? "";
};

export const ActionPrecisionQuestions = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { currentLanguage } = useLanguage();
    const { actionPlanAction, startingPost } = useMyActionPlanContext();
    const { actionPlan, actions } = useActionsContext();
    const answerPrecisionQuestion = useAnswerPrecisionQuestion();

    const precisionProperties = useMemo(
        () => getPrecisionProperties(actionPlanAction.action.ssqProperties ?? []),
        [actionPlanAction.action.ssqProperties],
    );

    const [isEditing, setIsEditing] = useState(false);
    const [draftValues, setDraftValues] = useState<Record<number, string>>({});
    const [pendingValue, setPendingValue] = useState<{ propertyId: number; value: string } | null>(null);
    const [pendingSharedActionTitles, setPendingSharedActionTitles] = useState<string>("");
    const [confirmOpen, setConfirmOpen] = useState(false);
    const isClosePressedRef = useRef(false);
    const pressedResetPropertyIdRef = useRef<number | null>(null);

    if (precisionProperties.length === 0 || startingPost === undefined || actionPlan === null) {
        return null;
    }

    const actionPlanId = actionPlan.id;
    const currentActionId = actionPlanAction.action.id;

    const savePropertyValue = (propertyId: number, value: string, isGenericValue: boolean) =>
        answerPrecisionQuestion.mutateAsync({ actionPlanId, ssqPropertyId: propertyId, value, isGenericValue });

    const requestSave = (propertyId: number, value: string) => {
        const baseline = readPropertyDisplayValue(
            precisionProperties.find((property) => property.id === propertyId)!,
            startingPost,
        );
        if (value === baseline) {
            return;
        }
        const otherActions = findOtherActionsSharingProperty(actions, propertyId, currentActionId);
        if (otherActions.length === 0) {
            void savePropertyValue(propertyId, value, false).catch(() => undefined);
            return;
        }
        setPendingValue({ propertyId, value });
        setPendingSharedActionTitles(otherActions.map((action) => action.title).join(", "));
        setConfirmOpen(true);
    };

    return (
        <Stack spacing={2} sx={{ marginBottom: theme.spacing(2) }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
                <Typography variant="h4">
                    {t(
                        "action_plan.multilevel.refinement_questions.title",
                        "Precision questions about your current diagnostic",
                    )}
                </Typography>
                {isEditing ?
                    <Button
                        variant="ternary"
                        onMouseDown={() => {
                            isClosePressedRef.current = true;
                        }}
                        onClick={() => {
                            isClosePressedRef.current = false;
                            setIsEditing(false);
                        }}
                    >
                        {t("common.close", "Close")}
                    </Button>
                :   <Button
                        variant="ternary"
                        onClick={() => {
                            setDraftValues({});
                            setIsEditing(true);
                        }}
                    >
                        {t("common.edit", "Edit")}
                    </Button>
                }
            </Stack>
            {!actionPlanAction.action.imageUrl && actionPlanAction.action.description && (
                <HTMLBox value={actionPlanAction.action.description} />
            )}
            {precisionProperties.map((property) => {
                const element = startingPost.elements?.find((elem) => elem.ssqPropertyId === property.id);
                const savedValue = readPropertyDisplayValue(property, startingPost);
                const displayValue = isEditing ? (draftValues[property.id] ?? savedValue) : savedValue;
                const unit = property.unit?.abbreviation ?? "";
                const isCustom = element?.isGenericValue === false;
                const isSaving =
                    answerPrecisionQuestion.isLoading &&
                    answerPrecisionQuestion.variables?.ssqPropertyId === property.id;

                return (
                    <Box key={property.id}>
                        <Typography variant="body-bold">{property.title ?? property.name}</Typography>
                        <Stack direction="row" alignItems="center" gap={1.5}>
                            <ActionParameterInput
                                value={displayValue}
                                type={property.field?.type === "number" ? "number" : "text"}
                                precision={property.precision}
                                locale={currentLanguage}
                                label={property.title ?? property.name}
                                disabled={!isEditing}
                                onChange={(value) => {
                                    if (!isEditing) {
                                        return;
                                    }
                                    setDraftValues((previous) => ({
                                        ...previous,
                                        [property.id]: value,
                                    }));
                                }}
                                onOptionsChange={() => undefined}
                                onBlur={() => {
                                    const isTakenOverByButton =
                                        isClosePressedRef.current || pressedResetPropertyIdRef.current === property.id;
                                    isClosePressedRef.current = false;
                                    pressedResetPropertyIdRef.current = null;
                                    if (!isEditing || isTakenOverByButton) {
                                        return;
                                    }
                                    requestSave(property.id, draftValues[property.id] ?? savedValue);
                                }}
                            />
                            {unit && <Typography variant="big-medium">{unit}</Typography>}
                            {isSaving && <Loading size={parseInt(theme.spacing(4))} sx={{ margin: 0 }} />}
                            {isCustom && isEditing && (
                                <Button
                                    variant="text"
                                    disabled={isSaving}
                                    onMouseDown={() => {
                                        pressedResetPropertyIdRef.current = property.id;
                                    }}
                                    onClick={() => {
                                        pressedResetPropertyIdRef.current = null;
                                        void savePropertyValue(
                                            property.id,
                                            property.genericValue ?? savedValue,
                                            true,
                                        ).catch(() => undefined);
                                        setDraftValues(({ [property.id]: _resetDraft, ...otherDrafts }) => otherDrafts);
                                    }}
                                >
                                    {t("action_plan.precision_questions.reset", "Reset to default")}
                                </Button>
                            )}
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                            {t("action_plan.precision_questions.default_value", "Default value: {{value}} {{unit}}", {
                                value: property.genericValue ?? "—",
                                unit,
                            })}
                        </Typography>
                    </Box>
                );
            })}
            <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
                <DialogTitle>
                    {t("action_plan.precision_questions.shared_confirm_title", "Update this shared answer?")}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body">
                        {t(
                            "action_plan.precision_questions.shared_confirm_body",
                            "This answer from your diagnostic is also used by: {{actions}}. Changing it will update their impact too.",
                            {
                                actions: pendingSharedActionTitles,
                            },
                        )}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirmOpen(false)}>{t("common.cancel", "Cancel")}</Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            if (pendingValue === null) {
                                return;
                            }
                            void savePropertyValue(pendingValue.propertyId, pendingValue.value, false)
                                .catch(() => undefined)
                                .finally(() => {
                                    setConfirmOpen(false);
                                    setPendingValue(null);
                                });
                        }}
                    >
                        {t("common.confirm", "Confirm")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};
